import AntIcon from "@/components/icons/AntIcon";
import { useWindowSize } from "@/utils/hooksUtils";
import { checkHasPathAccess, createMenuItem, createSubMenuItem, EXTERNAL_KEY_SUFFIX } from "@/utils/menuUtils";
import { cssVariables, parseFirstPartOfPathname } from "@/utils/utils";
import { MenuInfo } from "@rc-component/menu/es/interface";
import { ConfigProvider, Menu } from "antd";
import { ItemType, MenuDividerType, MenuItemType } from "antd/es/menu/interface";
import classNames from "classnames";
import { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Location, useLocation } from "react-router";
import { useGetPartnerConfigsAndSsoProperties } from "../../../common/api/queries";
import { ViewBreakpoints } from "../../../common/constants";
import { TopAgentType } from "../../../common/enums";
import {
  ADMIN_SUBMENU_KEY,
  AGENT_REPORTS_DIVIDER_KEY,
  COMMISSIONS_SETTINGS_DIVIDER_KEY,
  COMMISSIONS_SUBMENU_KEY,
  CONTRACT_FORMS_DIVIDER_KEY,
  CONTRACT_REPORTS_DIVIDER_KEY,
  SETTINGS_SUBMENU_KEY,
  SUBMENU_KEY,
} from "../../../common/security/constants";
import { Permission } from "../../../common/security/enums";
import { AFFILIATE_PARTNER_ROUTE_PATHS } from "../../../modules/affiliate/paths";
import { AgentStatus, FinancialSector } from "../../../modules/agent/enums";
import { AGENT_ROUTE_PATHS, AGENT_ROUTE_PREFIX } from "../../../modules/agent/paths";
import {
  useCurrentAgentAccount,
  useHasAnyPermission,
  useIsCurrentAgentTypePlus,
  usePermissions,
} from "../../../modules/auth/hooks";
import { CreateUserBugReportForm } from "../../../modules/bugreport/components/forms/CreateUserBugReportForm";
import { CALC_BLACKLIST_ROUTE_PATHS } from "../../../modules/calculator/blacklist/paths";
import { CALC_ROUTE_PATHS, CALC_ROUTE_PREFIX } from "../../../modules/calculator/paths";
import { ADMIN_CALC_ROUTE_PATHS } from "../../../modules/calculator/settings/paths";
import { CLIENT_ROUTE_PATHS, CLIENT_ROUTE_PREFIX } from "../../../modules/client/paths";
import { COMMISSIONS_BATCH_ROUTE_PATHS } from "../../../modules/commissions/batch/paths";
import { COMMISSIONS_LEVEL_ROUTE_PATHS } from "../../../modules/commissions/level/paths";
import { COMMISSIONS_OUTPUTS_PATHS } from "../../../modules/commissions/outputs/paths";
import { POSTPONED_COMMISSIONS_ROUTE_PATHS } from "../../../modules/commissions/postponed/paths";
import { COMMISSION_PROMOTIONS_ROUTE_PATHS } from "../../../modules/commissions/promotions/paths";
import { COMMISSIONS_SETTINGS_RANKS_ROUTE_PATHS } from "../../../modules/commissions/ranks/paths";
import { SPLIT_COMMISSIONS_ROUTE_PATHS } from "../../../modules/commissions/splitcommissions/paths";
import { COMPETITION_ROUTE_PATHS, COMPETITION_ROUTE_PREFIX } from "../../../modules/competition/path";
import { COMPLICITY_ROUTE_PATHS } from "../../../modules/complicity/paths";
import { CONTRACT_ROUTE_PATHS, CONTRACT_ROUTE_PREFIX } from "../../../modules/contract/paths";
import { CONTRACT_UPDATE_ROUTE_PATHS } from "../../../modules/contractupdate/paths";
import { COVERAGE_LIMIT_ROUTE_PATHS } from "../../../modules/coveragelimit/paths";
import { DASHBOARD_ROUTE_PATHS } from "../../../modules/dashboard/paths";
import { DOCUMENT_NODE_ROUTE_PATHS } from "../../../modules/documents/paths";
import { CONFIG_PROPERTY_ROUTE_PATHS } from "../../../modules/dynamicconfig/paths";
import { INSTITUTION_ROUTE_PATHS } from "../../../modules/institution/paths";
import { JOB_ROUTE_PATHS } from "../../../modules/jobs/paths";
import { LIFE_INSURENCE_TARIFF_ROUTE_PATHS } from "../../../modules/lifeinsurancetariff/paths";
import { useIsOverviewsEnabled } from "../../../modules/overviews/hooks";
import { OVERVIEWS_ROUTE_PATHS, OVERVIEWS_ROUTE_PREFIX } from "../../../modules/overviews/paths";
import { PartnerSsoSubType, PartnerSsoType } from "../../../modules/partner/enums";
import { PARTNER_ROUTE_PATHS } from "../../../modules/partner/paths";
import { PRODUCT_ROUTE_PATHS } from "../../../modules/product/paths";
import { ADMIN_USER_ROUTE_PATHS } from "../../../modules/user/paths";
import { VEHICLE_BRAND_ROUTE_PATHS } from "../../../modules/vehicle/brand/paths";
import { VEHICLE_ENUM_ROUTE_PATHS } from "../../../modules/vehicle/paths";
import styles from "./SideMenu.module.scss";

const BUG_REPORT_ITEM_KEY = "bug-report-menu-item";

interface MenuDividerProps extends Omit<MenuDividerType, "key" | "type"> {
  path: string;
  key?: React.Key;
  type?: "divider";
  permissions: Permission[];
}

const createMenuDivider = ({ path, permissions, ...dividerProps }: MenuDividerProps): MenuDividerType | undefined => {
  return checkHasPathAccess(path, permissions)
    ? {
        ...dividerProps,
        key: dividerProps.key || path,
        type: "divider",
        className: classNames(dividerProps?.className, styles.menuDivider),
      }
    : undefined;
};

type Props = {
  isMenuCollapsed?: boolean;
};

const SideMenu = ({ isMenuCollapsed }: Props) => {
  const { t } = useTranslation();

  const location = useLocation();

  const [selectedKeys, setSelectedKeys] = useState<string[]>([location.pathname]);
  const [openedKeys, setOpenedKeys] = useState<string[]>(() => [parseFirstPartOfPathname(location.pathname)]);
  const [bugReportFormOpen, setBugReportFormOpen] = useState(false);

  const agentUserAccount = useCurrentAgentAccount();
  const isTopAgentPlus = useIsCurrentAgentTypePlus();
  const permissions = usePermissions();
  const hasPartnersPermission = useHasAnyPermission(
    Permission.PARTNER_INSURANCE_COMPANIES,
    Permission.PARTNER_BANKS,
    Permission.PARTNER_SECURITIES_BROKERS,
    Permission.PARTNER_BUILDING_SAVINGS_BANKS,
    Permission.PARTNER_MANAGERIAL_PENSION_COMPANIES,
    Permission.PARTNER_SUPPLEMENTARY_PENSION_COMPANIES,
    Permission.PARTNER_OTHERS,
  );
  const isOverviewsEnabled = useIsOverviewsEnabled();

  const isTabletSize = useWindowSize().innerWidth < ViewBreakpoints.DESKTOP;

  useEffect(() => {
    setSelectedKeys([location.pathname]);
    setOpenedKeys(isTabletSize || isMenuCollapsed ? [] : [parseFirstPartOfPathname(location.pathname) + SUBMENU_KEY]);
  }, [location, isTabletSize]);

  const { data: partnerConfigs } = useGetPartnerConfigsAndSsoProperties(hasPartnersPermission);

  const eucsLink = partnerConfigs?.configs.find((config) => config.ssoType === PartnerSsoType.EUCS)?.link;
  const nsureLink = partnerConfigs?.configs.find((config) => config.ssoType === PartnerSsoType.NSURE)?.link;
  const vzdyViacLink = partnerConfigs?.configs.find((config) => config.ssoType === PartnerSsoType.VZDY_VIAC)?.link;
  const nonLifeCalcLink = partnerConfigs?.configs.find(
    (config) => config.ssoSubType === PartnerSsoSubType.FINGO_NON_LIFE_CALC,
  )?.link;
  const lifeCalcLink = partnerConfigs?.configs.find(
    (config) => config.ssoSubType === PartnerSsoSubType.FINGO_LIFE_CALC,
  )?.link;
  const loanCalcLink = partnerConfigs?.configs.find(
    (config) => config.ssoSubType === PartnerSsoSubType.FINGO_LOAN_CALC,
  )?.link;
  const investCalcLink = partnerConfigs?.configs.find(
    (config) => config.ssoSubType === PartnerSsoSubType.FINGO_INVEST_CALC,
  )?.link;

  const handleMenuItemClick = (clickInfo: MenuInfo) => {
    if (
      clickInfo.key !== location.pathname &&
      clickInfo.key !== BUG_REPORT_ITEM_KEY &&
      !clickInfo.key.endsWith(EXTERNAL_KEY_SUFFIX)
    ) {
      setSelectedKeys([clickInfo.key]);
    }
  };

  const getMenuItems = useCallback(
    (permissions: Permission[], routerLocation: Location) => {
      return [
        createMenuItem({
          path: DASHBOARD_ROUTE_PATHS.homepage.to,
          label: t("navigation.sideMenu.dashboard"),
          icon: <AntIcon type="dashboard" />,
          permissions,
          routerLocation,
          configProps: { topLevelMenuItem: true },
        }),
        vzdyViacLink
          ? createMenuItem({
              key: "vzdyViac",
              path: vzdyViacLink,
              label: t("navigation.sideMenu.vzdyViac"),
              icon: <AntIcon type="export" />,
              permissions,
              routerLocation,
              configProps: { externalPath: true },
            })
          : undefined,
        agentUserAccount?.agent.status === AgentStatus.BROKER_WITH_CONTRACT
          ? createSubMenuItem({
              path: COMPETITION_ROUTE_PREFIX + SUBMENU_KEY,
              label: t("navigation.header.competitions"),
              icon: <AntIcon type="trophy" />,
              topAgent: isTopAgentPlus ? TopAgentType.PLUS : undefined,
              permissions,
              children: [
                createMenuItem({
                  path: COMPETITION_ROUTE_PATHS.china.to,
                  label: t("navigation.sideMenu.competitions.china"),
                  icon: <AntIcon type="flag" />,
                  topAgent: isTopAgentPlus ? TopAgentType.PLUS : undefined,
                  permissions,
                  routerLocation,
                }),
                createMenuItem({
                  path: COMPETITION_ROUTE_PATHS.slovenia.to,
                  label: t("navigation.sideMenu.competitions.slovenia"),
                  icon: <AntIcon type="flag" />,
                  topAgent: isTopAgentPlus ? TopAgentType.PLUS : undefined,
                  permissions,
                  routerLocation,
                }),
              ].filter((item) => item !== undefined) as ItemType[],
            })
          : undefined,
        createSubMenuItem({
          path: CALC_ROUTE_PREFIX + SUBMENU_KEY,
          label: t("navigation.header.calc"),
          icon: <AntIcon type="calculator" />,
          permissions,
          children: [
            createMenuItem({
              path: CALC_ROUTE_PATHS.vehicle.to,
              label: t("navigation.sideMenu.calc.car"),
              icon: <AntIcon type="car" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: CALC_ROUTE_PATHS.realty.to,
              label: t("navigation.sideMenu.calc.realty"),
              icon: <AntIcon type="home" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: CALC_ROUTE_PATHS.travel.to,
              label: t("navigation.sideMenu.calc.travel"),
              icon: <AntIcon type="global" />,
              permissions,
              routerLocation,
            }),
            eucsLink
              ? createMenuItem({
                  key: "eucs",
                  path: eucsLink,
                  label: t("navigation.sideMenu.calc.eucs"),
                  icon: <AntIcon type="export" />,
                  permissions,
                  routerLocation,
                  configProps: { externalPath: true },
                  competenceCheck: FinancialSector.INSURANCES_OR_REINSURANCES,
                })
              : undefined,
            nsureLink
              ? createMenuItem({
                  key: "nsure",
                  path: nsureLink,
                  label: t("navigation.sideMenu.calc.nsure"),
                  icon: <AntIcon type="export" />,
                  permissions,
                  routerLocation,
                  configProps: { externalPath: true },
                })
              : undefined,
            nonLifeCalcLink
              ? createMenuItem({
                  key: "nonLifeCalc",
                  path: nonLifeCalcLink,
                  label: t("navigation.sideMenu.calc.fingoNonLifeCalc"),
                  icon: <AntIcon type="export" />,
                  permissions,
                  routerLocation,
                  configProps: { externalPath: true },
                })
              : undefined,
            lifeCalcLink
              ? createMenuItem({
                  key: "lifeCalc",
                  path: lifeCalcLink,
                  label: t("navigation.sideMenu.calc.fingoLifeCalc"),
                  icon: <AntIcon type="export" />,
                  permissions,
                  routerLocation,
                  configProps: { externalPath: true },
                })
              : undefined,
            loanCalcLink
              ? createMenuItem({
                  key: "loanCalc",
                  path: loanCalcLink,
                  label: t("navigation.sideMenu.calc.fingoLoanCalc"),
                  icon: <AntIcon type="export" />,
                  permissions,
                  routerLocation,
                  configProps: { externalPath: true },
                })
              : undefined,
            investCalcLink
              ? createMenuItem({
                  key: "investCalc",
                  path: investCalcLink,
                  label: t("navigation.sideMenu.calc.fingoInvestCalc"),
                  icon: <AntIcon type="export" />,
                  permissions,
                  routerLocation,
                  configProps: { externalPath: true },
                })
              : undefined,
            createMenuItem({
              path: CALC_ROUTE_PATHS.records.to,
              label: t("navigation.sideMenu.calc.records"),
              icon: <AntIcon type="file-search" />,
              permissions,
              routerLocation,
            }),
          ].filter((item) => item !== undefined) as ItemType[],
        }),
        createMenuItem({
          path: PARTNER_ROUTE_PATHS.configs.to,
          label: t("navigation.sideMenu.partners"),
          icon: <AntIcon type="appstore" />,
          permissions,
          routerLocation,
          configProps: { topLevelMenuItem: true },
        }),
        createSubMenuItem({
          path: CLIENT_ROUTE_PREFIX + SUBMENU_KEY,
          label: t("navigation.header.clients"),
          icon: <AntIcon type="team" />,
          permissions,
          children: [
            createMenuItem({
              path: CLIENT_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.clients.list"),
              icon: <AntIcon type="team" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: CLIENT_ROUTE_PATHS.create.to,
              label: t("navigation.sideMenu.clients.new"),
              icon: <AntIcon type="user-add" />,
              permissions,
              routerLocation,
            }),
          ].filter((item) => item !== undefined) as ItemType[],
        }),
        createSubMenuItem({
          path: CONTRACT_ROUTE_PREFIX + SUBMENU_KEY,
          label: t("navigation.header.contracts"),
          icon: <AntIcon type="audit" />,
          permissions,
          children: [
            createMenuItem({
              path: CONTRACT_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.contracts.list"),
              icon: <AntIcon type="database" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: CONTRACT_ROUTE_PATHS.create.to,
              label: t("navigation.sideMenu.contracts.new"),
              icon: <AntIcon type="plus-circle" />,
              permissions,
              routerLocation,
            }),
            createMenuDivider({ path: CONTRACT_REPORTS_DIVIDER_KEY, permissions }),
            createMenuItem({
              path: CONTRACT_ROUTE_PATHS.predictedCommissions.to,
              label: t("navigation.sideMenu.contracts.predictedCommissions"),
              icon: <AntIcon type="trophy" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: CONTRACT_ROUTE_PATHS.anniversaryDate.to,
              label: t("navigation.sideMenu.contracts.anniversaryDate"),
              icon: <AntIcon type="alert" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: CONTRACT_ROUTE_PATHS.fixationAnniversaryDate.to,
              label: t("navigation.sideMenu.contracts.fixationAnniversaryDate"),
              icon: <AntIcon type="exception" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: CONTRACT_ROUTE_PATHS.unpaid.to,
              label: t("navigation.sideMenu.contracts.unpaid"),
              icon: <AntIcon type="euro" />,
              permissions,
              routerLocation,
            }),
            createMenuDivider({ path: CONTRACT_FORMS_DIVIDER_KEY, permissions }),
            createMenuItem({
              path: CONTRACT_ROUTE_PATHS.forms.to,
              label: t("navigation.sideMenu.contracts.forms"),
              icon: <AntIcon type="form" />,
              permissions,
              routerLocation,
            }),
          ].filter((item) => item !== undefined) as ItemType[],
        }),
        createSubMenuItem({
          path: AGENT_ROUTE_PREFIX + SUBMENU_KEY,
          label: t("navigation.header.agents"),
          icon: <AntIcon type="user" />,
          permissions,
          children: [
            createMenuItem({
              path: AGENT_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.agents.list"),
              icon: <AntIcon type="team" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: AGENT_ROUTE_PATHS.tree.to,
              label: t("navigation.sideMenu.agents.tree"),
              icon: <AntIcon type="apartment" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: AGENT_ROUTE_PATHS.create.to,
              label: t("navigation.sideMenu.agents.new"),
              icon: <AntIcon type="user-add" />,
              permissions,
              routerLocation,
            }),
            createMenuDivider({ path: AGENT_REPORTS_DIVIDER_KEY, permissions }),
            createMenuItem({
              path: AGENT_ROUTE_PATHS.endingCompetences.to,
              label: t("navigation.sideMenu.agents.endingCompetences"),
              icon: <AntIcon type="file-done" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: AGENT_ROUTE_PATHS.pointsReport.to,
              label: t("navigation.sideMenu.agents.points"),
              icon: <AntIcon type="cluster" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: AGENT_ROUTE_PATHS.bailAccountsReport.to,
              label: t("navigation.sideMenu.agents.bailAccounts"),
              icon: <AntIcon type="plus-square" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: AGENT_ROUTE_PATHS.debtAccountsReport.to,
              label: t("navigation.sideMenu.agents.debtAccounts"),
              icon: <AntIcon type="minus-square" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: AGENT_ROUTE_PATHS.specialCommissionsReport.to,
              label: t("navigation.sideMenu.agents.specialCommissions"),
              icon: <AntIcon type="dollar" />,
              permissions,
              routerLocation,
            }),
          ].filter((item) => item !== undefined) as ItemType[],
        }),
        isOverviewsEnabled
          ? createSubMenuItem({
              path: OVERVIEWS_ROUTE_PREFIX + SUBMENU_KEY,
              label: t("navigation.header.overviews"),
              icon: <AntIcon type="projection" />,
              topAgent: isTopAgentPlus ? TopAgentType.PLUS : undefined,
              permissions,
              children: [
                createMenuItem({
                  path: OVERVIEWS_ROUTE_PATHS.businessCoefficients.to,
                  label: t("navigation.sideMenu.overviews.businessCoefficients"),
                  icon: <AntIcon type="fund-view" />,
                  topAgent: isTopAgentPlus ? TopAgentType.PLUS : undefined,
                  permissions,
                  routerLocation,
                }),
              ].filter((item) => item !== undefined) as ItemType[],
            })
          : undefined,
        createMenuItem({
          path: DOCUMENT_NODE_ROUTE_PATHS.list.to,
          label: t("navigation.header.documents"),
          icon: <AntIcon type="folder" />,
          permissions,
          routerLocation,
          configProps: { topLevelMenuItem: true },
        }),
        createSubMenuItem({
          path: COMMISSIONS_SUBMENU_KEY,
          label: t("navigation.header.commissions"),
          icon: <AntIcon type="trophy" />,
          permissions,
          children: [
            createMenuItem({
              path: COMMISSIONS_BATCH_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.commissions.batchesList"),
              icon: <AntIcon type="dollar" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: COMMISSIONS_OUTPUTS_PATHS.list.to,
              label: t("navigation.sideMenu.commissions.outputsList"),
              icon: <AntIcon type="euro" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: POSTPONED_COMMISSIONS_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.commissions.postponedCommissions"),
              icon: <AntIcon type="pause" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: SPLIT_COMMISSIONS_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.commissions.splitCommissions"),
              icon: <AntIcon type="swap" />,
              topAgent: isTopAgentPlus ? TopAgentType.PLUS : undefined,
              permissions,
              routerLocation,
            }),
            createMenuDivider({ path: COMMISSIONS_SETTINGS_DIVIDER_KEY, permissions }),
            createMenuItem({
              path: COMMISSION_PROMOTIONS_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.commissions.promotions"),
              icon: <AntIcon type="up-circle" />,
              topAgent: isTopAgentPlus ? TopAgentType.PLUS : undefined,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: COMMISSIONS_LEVEL_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.commissions.levels"),
              icon: <AntIcon type="fork" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: COMMISSIONS_SETTINGS_RANKS_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.commissions.ranks"),
              icon: <AntIcon type="crown" />,
              permissions,
              routerLocation,
            }),
          ].filter((item) => item !== undefined) as ItemType[],
        }),
        createSubMenuItem({
          path: ADMIN_SUBMENU_KEY,
          label: t("navigation.header.admin"),
          icon: <AntIcon type="safety-certificate" />,
          permissions,
          children: [
            createMenuItem({
              path: ADMIN_USER_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.admin.users"),
              icon: <AntIcon type="user-switch" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: INSTITUTION_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.admin.institutions"),
              icon: <AntIcon type="bank" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: PRODUCT_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.admin.products"),
              icon: <AntIcon type="box" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: AFFILIATE_PARTNER_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.admin.affiliatePartners"),
              icon: <AntIcon type="deployment-unit" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: AGENT_ROUTE_PATHS.directorates.to,
              label: t("navigation.sideMenu.admin.agentDirectorates"),
              icon: <AntIcon type="partition" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: AGENT_ROUTE_PATHS.education.to,
              label: t("navigation.sideMenu.admin.agentEducation"),
              topAgent: isTopAgentPlus ? TopAgentType.PLUS : undefined,
              icon: <AntIcon type="reconciliation" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: ADMIN_CALC_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.admin.calculators"),
              icon: <AntIcon type="calculator" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: CONTRACT_UPDATE_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.admin.contractUpdates"),
              icon: <AntIcon type="cloud-sync" />,
              permissions,
              routerLocation,
            }),
          ].filter((item) => item !== undefined) as ItemType[],
        }),
        createSubMenuItem({
          path: SETTINGS_SUBMENU_KEY,
          label: t("navigation.header.settings"),
          icon: <AntIcon type="settings" />,
          permissions,
          children: [
            createMenuItem({
              path: LIFE_INSURENCE_TARIFF_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.settings.lifeInsuranceTariffs"),
              icon: <AntIcon type="container" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: COVERAGE_LIMIT_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.settings.coverageLimits"),
              icon: <AntIcon type="reconciliation" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: COMPLICITY_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.settings.complicities"),
              icon: <AntIcon type="sister-node" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: VEHICLE_BRAND_ROUTE_PATHS.brands.to,
              label: t("navigation.sideMenu.settings.vehicleBrandsAndModels"),
              icon: <AntIcon type="heat-map" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: VEHICLE_ENUM_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.settings.vehicleEnums"),
              icon: <AntIcon type="car" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: JOB_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.settings.jobs"),
              icon: <AntIcon type="robot" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: CALC_BLACKLIST_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.settings.blacklist"),
              icon: <AntIcon type="calculator" />,
              permissions,
              routerLocation,
            }),
            createMenuItem({
              path: CONFIG_PROPERTY_ROUTE_PATHS.list.to,
              label: t("navigation.sideMenu.settings.configProperties"),
              icon: <AntIcon type="key" />,
              permissions,
              routerLocation,
            }),
          ].filter((item) => item !== undefined) as ItemType[],
        }),
        isTopAgentPlus
          ? createMenuItem({
              key: "freelo",
              path: "https://app.freelo.io",
              label: t("navigation.sideMenu.requests"),
              icon: <AntIcon type="export" />,
              permissions,
              routerLocation,
              configProps: { externalPath: true },
            })
          : undefined,
        agentUserAccount?.adminOrSystemAdmin
          ? ({
              key: BUG_REPORT_ITEM_KEY,
              label: t("navigation.sideMenu.bugReport"),
              icon: <AntIcon type="bug" color="red" />,
              onClick: () => setBugReportFormOpen(true),
            } as MenuItemType)
          : undefined,
      ].filter((item) => item !== undefined) as ItemType[];
    },
    [
      permissions,
      location,
      agentUserAccount?.adminOrSystemAdmin,
      eucsLink,
      nsureLink,
      vzdyViacLink,
      nonLifeCalcLink,
      lifeCalcLink,
      loanCalcLink,
      investCalcLink,
    ],
  );

  return (
    <>
      <ConfigProvider
        theme={{ components: { Menu: { colorSplit: cssVariables.colorGrey, itemHeight: 44, itemMarginBlock: 8 } } }}
      >
        <Menu
          theme="dark"
          mode="inline"
          items={getMenuItems(permissions, location)}
          selectedKeys={selectedKeys}
          openKeys={openedKeys}
          onClick={handleMenuItemClick}
          onOpenChange={(openKeys) => setOpenedKeys(openKeys[1] ? [openKeys[1]] : openKeys)}
          className={styles.menuWrapper}
        />
      </ConfigProvider>

      {agentUserAccount?.adminOrSystemAdmin && (
        <CreateUserBugReportForm open={bugReportFormOpen} onFormCancel={() => setBugReportFormOpen(false)} />
      )}
    </>
  );
};

export default memo(SideMenu);
